/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
  useFormikContext,
} from 'formik'
import React, {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { FiArrowRightCircle, FiClock, FiPlus, FiX } from 'react-icons/fi'
import {
  MdBalance,
  MdDeleteForever,
  MdOutlineHealthAndSafety,
} from 'react-icons/md'

import usePersistedState from '../../hooks/usePersistedState'

import Button from '../../components/Button'
import Header from '../../components/Header'
import Input from '../../components/Input'
import InputHidden from '../../components/InputHidden'
import InputSelect from '../../components/InputSelect'
import ModalBox from '../../components/Modal'

import {
  FaFileContract,
  FaGraduationCap,
  FaRegBuilding,
  FaRegCalendarCheck,
  FaRegCalendarTimes,
} from 'react-icons/fa'
import { RadioGroup } from '../../components/RadioGroup/radio-group'
import { ServiceTimeData, UserData } from '../../utils/interfaces'
import {
  NaturezaJuridicaList,
  ResponsavelTempoList,
  TipoVinculoList,
} from '../../utils/listasFixas'

import { FileUploader } from '../../components/upload-file/file-uploader'
import { uploadFile } from '../../services/upload-file-to-aws'

type ServiceTimeFormValues = {
  addNew: boolean
  organizationDcr: string
  timeResponsible: string
  teaching: string
  health: string
  typeLink: string
  legalNature: string
  startDate: string
  endDate: string
  positionDcr: string

  /** controle do select de documento no form */
  selectedDocCode: string
}

const DOC_TIME_CTC = '12' // CTC
const DOC_TIME_CNIS = '16' // CNIS
const DOC_TIME_CTPS = '3' // CTPS

type ServiceTimeAttachedDoc = {
  id: string
  docCode: string
  docLabel: string
  name: string
  type: string
  lastModified: number
  size: number
  url: string
}

type ServiceTimeWithDocs = ServiceTimeData & {
  attachedDocs?: ServiceTimeAttachedDoc[]
}

type DocumentItem = {
  codDocumento: string
  dcrDocumento: string
}

type DocumentFiles = {
  [codDocumento: string]: File[]
}

const fileKey = (f: File) => `${f.name}__${f.size}__${f.lastModified}`
const makeDocId = (docCode: string, f: File) =>
  `${docCode}__${f.name}__${f.lastModified}__${f.size}`

function safeTrim(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function isServiceTimeDataComplete(t: ServiceTimeWithDocs): boolean {
  const hasOrg = safeTrim(t.organizationDcr).length > 0
  const hasResp = safeTrim(t.timeResponsible).length > 0
  const hasTeaching = safeTrim(t.teaching).length > 0
  const hasHealth = safeTrim(t.health).length > 0
  const hasType = safeTrim(t.typeLink).length > 0
  const hasNature = safeTrim(t.legalNature).length > 0
  const hasStart = safeTrim(t.startDate).length > 0
  // endDate pode ser vazio
  const hasPosition = safeTrim(t.positionDcr).length > 0

  return (
    hasOrg &&
    hasResp &&
    hasTeaching &&
    hasHealth &&
    hasType &&
    hasNature &&
    hasStart &&
    hasPosition
  )
}

function docsStatusForServiceTime(t: ServiceTimeWithDocs): { isOk: boolean } {
  const docs = t.attachedDocs ?? []
  const hasOne =
    docs.some((d) => d.docCode === DOC_TIME_CTC) ||
    docs.some((d) => d.docCode === DOC_TIME_CNIS) ||
    docs.some((d) => d.docCode === DOC_TIME_CTPS)

  return { isOk: hasOne }
}

function ServiceTimeDocsSection({
  draftDocumentFiles,
  setDraftDocumentFiles,
  isUploading,
  setDraftError,
  onDraftAttachedListChange,
  onDraftOkChange,
}: {
  draftDocumentFiles: DocumentFiles
  setDraftDocumentFiles: React.Dispatch<React.SetStateAction<DocumentFiles>>
  isUploading: boolean
  setDraftError: (msg: string) => void
  onDraftAttachedListChange: (
    list: Array<{ docCode: string; docLabel: string; file: File }>,
  ) => void
  onDraftOkChange: (payload: { isOk: boolean }) => void
}): JSX.Element {
  const { values, setFieldValue } = useFormikContext<ServiceTimeFormValues>()

  const docOptions = useMemo<DocumentItem[]>(
    () => [
      { codDocumento: DOC_TIME_CTC, dcrDocumento: 'CTC' },
      { codDocumento: DOC_TIME_CNIS, dcrDocumento: 'CNIS' },
      { codDocumento: DOC_TIME_CTPS, dcrDocumento: 'CTPS' },
    ],
    [],
  )

  useEffect(() => {
    const current = values.selectedDocCode
    const stillExists =
      !!current && docOptions.some((d) => d.codDocumento === current)
    if (stillExists) return

    const next = docOptions[0]?.codDocumento ?? ''
    setFieldValue('selectedDocCode', next, false)
    setDraftError('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docOptions])

  const selectedDocCode = values.selectedDocCode

  const selectedFiles = useMemo(() => {
    return draftDocumentFiles[selectedDocCode] ?? []
  }, [draftDocumentFiles, selectedDocCode])

  const setSelectedDocFiles = useCallback(
    (filesOrUpdater: File[] | ((prev: File[]) => File[])) => {
      if (!selectedDocCode) return

      setDraftDocumentFiles((prev) => {
        const current = prev[selectedDocCode] ?? []
        const rawNext =
          typeof filesOrUpdater === 'function'
            ? filesOrUpdater(current)
            : filesOrUpdater

        // 1 arquivo por doc
        const one = rawNext?.[0] ? [rawNext[0]] : []

        // remove duplicados
        const seen = new Set<string>()
        const next = one.filter((file) => {
          const k = fileKey(file)
          if (seen.has(k)) return false
          seen.add(k)
          return true
        })

        return { ...prev, [selectedDocCode]: next }
      })
    },
    [selectedDocCode, setDraftDocumentFiles],
  )

  const draftAttachedList = useMemo(() => {
    const items: Array<{ docCode: string; docLabel: string; file: File }> = []
    docOptions.forEach((doc) => {
      const files = draftDocumentFiles[doc.codDocumento] ?? []
      files.forEach((f) => {
        items.push({
          docCode: doc.codDocumento,
          docLabel: doc.dcrDocumento,
          file: f,
        })
      })
    })
    return items
  }, [docOptions, draftDocumentFiles])

  useEffect(() => {
    onDraftAttachedListChange(draftAttachedList)
  }, [draftAttachedList, onDraftAttachedListChange])

  const hasAny =
    (draftDocumentFiles[DOC_TIME_CTC] ?? []).length > 0 ||
    (draftDocumentFiles[DOC_TIME_CNIS] ?? []).length > 0 ||
    (draftDocumentFiles[DOC_TIME_CTPS] ?? []).length > 0

  useEffect(() => {
    onDraftOkChange({ isOk: hasAny })
  }, [hasAny, onDraftOkChange])

  const removeDraftItem = useCallback(
    (docCode: string) => {
      setDraftDocumentFiles((prev) => {
        const next = { ...prev }
        delete next[docCode]
        return next
      })
    },
    [setDraftDocumentFiles],
  )

  const selectedOptionLabel = useMemo(() => {
    return (
      docOptions.find((d) => d.codDocumento === selectedDocCode)
        ?.dcrDocumento ?? ''
    )
  }, [docOptions, selectedDocCode])

  return (
    <div className="w-full mt-2">
      <div className="flex flex-col tablet:flex-row tablet:items-end">
        <div className="w-full tablet:w-2/3">
          <InputSelect
            name="selectedDocCode"
            placeholder="Documento"
            options={docOptions.map((doc) => ({
              label: doc.dcrDocumento,
              value: doc.codDocumento,
            }))}
          />
        </div>

        <div className="text-xs text-[#333] ">
          <p className="text-[#C53030] font-semibold">
            Obrigatório anexar CTC ou CNIS ou CTPS.
          </p>
          <p className="text-[#C53030] ">Tamanho máximo: 5MB por arquivo.</p>
        </div>

        <div className="w-full tablet:w-1/3 flex flex-col items-center">
          <FileUploader
            files={selectedFiles}
            setFiles={setSelectedDocFiles}
            setFieldValue={() => {}}
            fieldName={`draft.docs.${selectedDocCode}`}
          >
            <FileUploader.Button
              disabled={!selectedDocCode || isUploading}
              className="min-w-fit"
              color="green"
              width="small"
              fontSize="small"
            >
              <div className="flex flex-row gap-3 font-semibold text-[14px] items-center justify-center">
                <FiPlus size={20} />
                Anexar {selectedOptionLabel || 'documento'}
              </div>
            </FileUploader.Button>
          </FileUploader>
        </div>
      </div>

      {draftAttachedList.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-col gap-2">
            {draftAttachedList.map((item) => (
              <div
                key={`${item.docCode}__${item.file.name}__${item.file.lastModified}__${item.file.size}`}
                className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-2 rounded-md p-3"
              >
                <div className="flex flex-wrap justify-between w-full">
                  <div className="min-w-0">
                    <p
                      className="text-sm font-semibold truncate"
                      title={item.file.name}
                    >
                      {item.file.name}
                    </p>
                    <p
                      className="text-xs text-tertiary-3 truncate"
                      title={item.docLabel}
                    >
                      {item.docLabel} • {item.file.type || 'tipo desconhecido'}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                    onClick={() => removeDraftItem(item.docCode)}
                    title="Remover"
                    disabled={isUploading}
                  >
                    <MdDeleteForever size={18} />
                    Remover
                  </button>
                </div>
                <hr />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const ServiceTime: React.FC = () => {
  const [userData] = usePersistedState<UserData>('userData', {} as UserData)

  const [servicesTime, setServicesTime] = usePersistedState<
    ServiceTimeWithDocs[]
  >('servicestimeGroup', [])

  const [thisTimeServiceData] = useState<ServiceTimeData>({} as ServiceTimeData)

  const navigate = useNavigate()
  const location = useLocation()
  const formikRef = useRef<FormikProps<ServiceTimeFormValues>>(null)

  const timeFormBoxRef = useRef<HTMLDivElement | null>(null)
  const shouldScrollToTimeBoxRef = useRef(false)

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [draftDocumentFiles, setDraftDocumentFiles] = useState<DocumentFiles>(
    {},
  )
  const [draftAttachedList, setDraftAttachedList] = useState<
    Array<{ docCode: string; docLabel: string; file: File }>
  >([])
  const [draftStatus, setDraftStatus] = useState<{ isOk: boolean }>({
    isOk: false,
  })
  const [draftDocsError, setDraftDocsError] = useState('')
  const [isUploadingDraft, setIsUploadingDraft] = useState(false)

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        organizationDcr: Yup.string().required('Campo obrigatório.'),
        timeResponsible: Yup.string().required('Campo obrigatório.'),
        teaching: Yup.string().required('Campo obrigatório.'),
        health: Yup.string().required('Campo obrigatório.'),
        typeLink: Yup.string().required('Campo obrigatório.'),
        legalNature: Yup.string().required('Campo obrigatório.'),
        startDate: Yup.string().required('Campo obrigatório.'),
        endDate: Yup.string(),
        positionDcr: Yup.string().required('Campo obrigatório.'),
        selectedDocCode: Yup.string().notRequired(),
      }),
    [userData],
  )

  const initialValues = useMemo<ServiceTimeFormValues>(() => {
    const t = editingIndex !== null ? servicesTime[editingIndex] : undefined

    return {
      addNew: false,
      organizationDcr:
        t?.organizationDcr ?? thisTimeServiceData.organizationDcr,
      timeResponsible:
        t?.timeResponsible ?? thisTimeServiceData.timeResponsible,
      teaching: t?.teaching ?? thisTimeServiceData.teaching,
      health: t?.health ?? thisTimeServiceData.health,
      typeLink: t?.typeLink ?? thisTimeServiceData.typeLink,
      legalNature: t?.legalNature ?? thisTimeServiceData.legalNature,
      startDate: t?.startDate
        ? t.startDate.split('-').reverse().join('/')
        : thisTimeServiceData.startDate
          ? thisTimeServiceData.startDate.split('-').reverse().join('/')
          : '',
      endDate: t?.endDate
        ? t.endDate.split('-').reverse().join('/')
        : thisTimeServiceData.endDate
          ? thisTimeServiceData.endDate.split('-').reverse().join('/')
          : '',
      positionDcr: t?.positionDcr ?? thisTimeServiceData.positionDcr,

      selectedDocCode: DOC_TIME_CTC,
    }
  }, [editingIndex, servicesTime, thisTimeServiceData])

  const emptyValues = useMemo<ServiceTimeFormValues>(
    () => ({
      addNew: false,
      organizationDcr: '',
      timeResponsible: '',
      teaching: '',
      health: '',
      typeLink: '',
      legalNature: '',
      startDate: '',
      endDate: '',
      positionDcr: '',
      selectedDocCode: DOC_TIME_CTC,
    }),
    [],
  )

  const scrollToTimeBox = useCallback(() => {
    const el =
      timeFormBoxRef.current ||
      (document.getElementById('timeFormBox') as HTMLDivElement | null)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const clearDraft = useCallback(() => {
    setDraftDocumentFiles({})
    setDraftAttachedList([])
    setDraftDocsError('')
    setDraftStatus({ isOk: false })
  }, [])

  const makeStoredDoc = useCallback(
    async (
      docCode: string,
      docLabel: string,
      file: File,
    ): Promise<ServiceTimeAttachedDoc> => {
      const url = await uploadFile({ file })
      return {
        id: makeDocId(docCode, file),
        docCode,
        docLabel,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        size: file.size,
        url,
      }
    },
    [],
  )

  const handleEdit = useCallback(
    (index: number) => {
      setEditingIndex(index)
      clearDraft()
      shouldScrollToTimeBoxRef.current = true
    },
    [clearDraft],
  )

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null)
    clearDraft()
    formikRef.current?.resetForm()
  }, [clearDraft])

  const handleRemove = useCallback(
    (id: number) => {
      setServicesTime((prev) => prev.filter((_, idx) => idx !== id))
      setEditingIndex((prev) => (prev === id ? null : prev))
    },
    [setServicesTime],
  )

  const handleSubmit = useCallback(
    async (
      formData: ServiceTimeFormValues,
      helpers: FormikHelpers<ServiceTimeFormValues>,
    ) => {
      const nextServiceTimeBase: ServiceTimeWithDocs = {
        organizationDcr: formData.organizationDcr,
        timeResponsible: formData.timeResponsible,
        teaching: formData.teaching,
        health: formData.health,
        typeLink: formData.typeLink,
        legalNature: formData.legalNature,
        startDate: formData.startDate?.split('/').reverse().join('-'),
        endDate: formData.endDate?.split('/').reverse().join('-'),
        positionDcr: formData.positionDcr,
      } as ServiceTimeWithDocs

      const existingDocs =
        editingIndex !== null
          ? (servicesTime[editingIndex]?.attachedDocs ?? [])
          : []

      const existingOk =
        existingDocs.some((d) => d.docCode === DOC_TIME_CTC) ||
        existingDocs.some((d) => d.docCode === DOC_TIME_CNIS) ||
        existingDocs.some((d) => d.docCode === DOC_TIME_CTPS)

      const willBeOk = existingOk || draftStatus.isOk

      if (!willBeOk) {
        setDraftDocsError(
          'Anexe CTC ou CNIS ou CTPS para salvar este tempo de contribuição.',
        )
        return
      }

      setDraftDocsError('')
      setIsUploadingDraft(true)

      try {
        const uploads: ServiceTimeAttachedDoc[] = []

        for (const item of draftAttachedList) {
          try {
            const uploadedDoc = await makeStoredDoc(
              item.docCode,
              item.docLabel,
              item.file,
            )
            uploads.push(uploadedDoc)
          } catch (err: unknown) {
            setDraftDocumentFiles((prev) => {
              const next = { ...prev }
              delete next[item.docCode]
              return next
            })

            const msg =
              err instanceof Error ? err.message : 'Falha ao enviar documentos.'

            setDraftDocsError(msg)
            return
          }
        }

        const docsMap = new Map<string, ServiceTimeAttachedDoc>()
        ;[...existingDocs, ...uploads].forEach((d) => docsMap.set(d.id, d))

        const nextServiceTime: ServiceTimeWithDocs = {
          ...nextServiceTimeBase,
          attachedDocs: Array.from(docsMap.values()),
        }

        setServicesTime((prev) => {
          if (editingIndex === null) return [...prev, nextServiceTime]
          return prev.map((t, i) =>
            i === editingIndex ? { ...t, ...nextServiceTime } : t,
          )
        })

        if (editingIndex !== null) {
          setEditingIndex(null)
          clearDraft()
          helpers.resetForm({ values: emptyValues })
          navigate(location.pathname)
          return
        }

        if (formData.addNew) {
          shouldScrollToTimeBoxRef.current = true
          clearDraft()
          helpers.resetForm({ values: emptyValues })
          return
        }

        navigate('/supporting-documents')
      } finally {
        setIsUploadingDraft(false)
      }
    },
    [
      clearDraft,
      draftAttachedList,
      draftStatus.isOk,
      editingIndex,
      emptyValues,
      initialValues,
      location.pathname,
      makeStoredDoc,
      navigate,
      servicesTime,
      setServicesTime,
    ],
  )

  const handlePrimaryAction = useCallback(async () => {
    const formik = formikRef.current
    if (!formik) return

    const touched = Object.keys(formik.values).reduce<Record<string, boolean>>(
      (acc, key) => {
        acc[key] = true
        return acc
      },
      {},
    )

    await formik.setTouched(touched, true)

    const isEditing = editingIndex !== null
    await formik.setFieldValue('addNew', !isEditing, false)
    await formik.submitForm()
  }, [editingIndex])

  useEffect(() => {
    if (!shouldScrollToTimeBoxRef.current) return

    const t = window.setTimeout(() => {
      scrollToTimeBox()
      shouldScrollToTimeBoxRef.current = false
    }, 50)

    return () => window.clearTimeout(t)
  }, [editingIndex, servicesTime.length, scrollToTimeBox])

  const canProceed = useMemo(() => {
    if (servicesTime.length === 0) return true
    return servicesTime.every((t) => {
      const dataOk = isServiceTimeDataComplete(t)
      const docsOk = docsStatusForServiceTime(t).isOk
      return dataOk && docsOk
    })
  }, [servicesTime])

  const missingList = useMemo(() => {
    const issues: Array<{ index: number; msg: string }> = []
    servicesTime.forEach((t, index) => {
      const dataOk = isServiceTimeDataComplete(t)
      const docsOk = docsStatusForServiceTime(t).isOk

      if (!dataOk) issues.push({ index, msg: 'Dados obrigatórios pendentes' })
      if (!docsOk)
        issues.push({ index, msg: 'Docs pendentes (CTC ou CNIS ou CTPS)' })
    })
    return issues
  }, [servicesTime])

  const removeDocFromServiceTime = useCallback(
    (timeIndex: number, docId: string) => {
      setServicesTime((prev) =>
        prev.map((t, i) => {
          if (i !== timeIndex) return t
          const nextDocs = (t.attachedDocs ?? []).filter((d) => d.id !== docId)
          return { ...t, attachedDocs: nextDocs }
        }),
      )
    },
    [setServicesTime],
  )

  return (
    <>
      <Header />
      <div className="mx-auto mb-[15px] flex max-w-[600px] flex-col items-center px-[5px] max-[766px]:max-w-[550px]">
        <div className="mx-[5px] mb-[20px] w-full rounded-[8px] bg-white p-[30px_25px] shadow-[0px_2px_4px_rgba(0,0,0,0.05),0px_6px_12px_rgba(61,69,67,0.05)] max-[766px]:p-[20px_15px]">
          <div className="w-full">
            {servicesTime.length > 0 ? (
              <div className="relative my-[10px] w-full p-[15px_25px_10px] max-[766px]:p-[20px_15px_10px]">
                <h3 className="mb-[15px] flex justify-center text-center text-[18px] font-bold text-[#0071ba]">
                  Tempos de contribuição declarados:
                </h3>

                {servicesTime.map((info, index) => {
                  const isEditing = editingIndex === index

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index} className="relative w-full">
                      <div className="relative">
                        <div className="my-[6px]">
                          <label>Empresa / Organização: </label>
                          <strong>{info.organizationDcr}</strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Responsável pelo tempo: </label>
                          <strong>
                            {
                              ResponsavelTempoList.filter(
                                (v) => v.value === String(info.timeResponsible),
                              )[0]?.label
                            }
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Professor de Educação Básica: </label>
                          <strong>
                            {info.teaching === 'S' ? 'Sim' : 'Não'}
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Área da saúde: </label>
                          <strong>{info.health === 'S' ? 'Sim' : 'Não'}</strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Vínculo: </label>
                          <strong>
                            {
                              TipoVinculoList.filter(
                                (v) => v.value === String(info.typeLink),
                              )[0]?.label
                            }
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Natureza jurídica: </label>
                          <strong>
                            {
                              NaturezaJuridicaList.filter(
                                (v) => v.value === String(info.legalNature),
                              )[0]?.label
                            }
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Admissão / Posse: </label>
                          <strong>
                            {(info.startDate ?? '')
                              .split('-')
                              .reverse()
                              .join('/')}
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Demissão / Exoneração: </label>
                          <strong>
                            {info.endDate
                              ? info.endDate.split('-').reverse().join('/')
                              : ''}
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Documenos anexados: </label>
                          {(info.attachedDocs?.length ?? 0) > 0 ? (
                            <div className="px-2">
                              {(info.attachedDocs ?? []).map((doc) => (
                                <div
                                  key={doc.id}
                                  className="flex items-center justify-between gap-2 py-1"
                                >
                                  <strong
                                    className="truncate"
                                    title={`${doc.docLabel} | ${doc.name}`}
                                  >
                                    {doc.docLabel} | {doc.name}
                                  </strong>

                                  <button
                                    type="button"
                                    className="inline-flex items-center justify-center rounded-full border px-2 py-1 hover:border-[#C53030] hover:text-[#C53030]"
                                    title="Remover documento"
                                    onClick={() =>
                                      removeDocFromServiceTime(index, doc.id)
                                    }
                                    disabled={isUploadingDraft}
                                  >
                                    <FiX size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <strong className="text-red-600">
                              Não anexado
                            </strong>
                          )}
                        </div>

                        <div className="w-full flex justify-center mt-4 gap-3 flex-wrap">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#0071ba] hover:text-[#0071ba]"
                            onClick={() => handleEdit(index)}
                            aria-label={`Editar ${index}`}
                            title="Editar"
                            disabled={isUploadingDraft}
                          >
                            <FiClock size={18} />
                            {isEditing ? 'Editando...' : 'Editar'}
                          </button>

                          <button
                            type="button"
                            className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                            onClick={() => handleRemove(index)}
                            aria-label={`Remover ${index}`}
                            title="Remover"
                            disabled={isUploadingDraft}
                          >
                            <MdDeleteForever size={18} />
                            Remover
                          </button>
                        </div>
                      </div>

                      <div className="my-[10px] h-[1px] w-full bg-[#E5E7EB]" />
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>

          <h3
            id="timeFormBox"
            ref={timeFormBoxRef}
            className="mb-[15px] flex justify-center text-center text-[18px] text-[#0071ba] font-bold"
          >
            {editingIndex !== null
              ? 'Editar tempo de contribuição'
              : 'Informe seu tempo de contribuição anterior:'}
          </h3>

          <p className="mx-[12px] my-[16px] text-justify">
            A declaração do tempo de contribuição anterior, seja no Regime Geral
            ou para outros Regimes Próprios de Previdência Social, é importante
            para provisionamento de recursos para o pagamento dos benefícios
            previdenciários futuros. Os tempos aqui declarados deverão ser
            comprovados, na ocasião da solicitação do benefício, através de CTC
            – Certidão de Tempo de Contribuição.
          </p>

          <Formik
            innerRef={formikRef}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {() => (
              <Form className="flex w-full flex-col items-center">
                <InputHidden name="addNew" type="hidden" />

                <Input
                  placeholder="Empresa / Organização"
                  name="organizationDcr"
                  icon={FaRegBuilding}
                />

                <InputSelect
                  icon={FiClock}
                  name="timeResponsible"
                  placeholder="Responsável pelo tempo"
                  options={ResponsavelTempoList}
                />

                <RadioGroup
                  name="teaching"
                  icon={FaGraduationCap}
                  placeholder="Professor de Educação Básica?"
                  options={[
                    { label: 'Sim', value: 'S' },
                    { label: 'Não', value: 'N' },
                  ]}
                />

                <RadioGroup
                  name="health"
                  icon={MdOutlineHealthAndSafety}
                  placeholder="Área da saúde?"
                  options={[
                    { label: 'Sim', value: 'S' },
                    { label: 'Não', value: 'N' },
                  ]}
                />

                <InputSelect
                  icon={FaFileContract}
                  name="typeLink"
                  placeholder="Vínculo"
                  options={TipoVinculoList}
                />

                <InputSelect
                  icon={MdBalance}
                  name="legalNature"
                  placeholder="Natureza jurídica"
                  options={NaturezaJuridicaList}
                />

                <Input
                  icon={FaRegCalendarCheck}
                  name="startDate"
                  placeholder="Admissão / Posse"
                  mask="date"
                />

                <Input
                  icon={FaRegCalendarTimes}
                  name="endDate"
                  placeholder="Demissão / Exoneração"
                  mask="date"
                />

                <Input name="positionDcr" placeholder="Descrição do cargo" />

                {/* DOCS OBRIGATÓRIOS (CTC ou CNIS ou CTPS) - mesmo padrão do Beneficiary */}
                <ServiceTimeDocsSection
                  draftDocumentFiles={draftDocumentFiles}
                  setDraftDocumentFiles={setDraftDocumentFiles}
                  isUploading={isUploadingDraft}
                  setDraftError={setDraftDocsError}
                  onDraftAttachedListChange={setDraftAttachedList}
                  onDraftOkChange={setDraftStatus}
                />
              </Form>
            )}
          </Formik>

          <hr className="my-4" />

          <div className="flex flex-col items-center w-full justify-center flex-wrap">
            <Button
              type="button"
              color="blue"
              className="min-w-fit gap-3 px-10"
              onClick={handlePrimaryAction}
              disabled={isUploadingDraft}
              title={
                !draftStatus.isOk
                  ? 'Anexe CTC ou CNIS ou CTPS para habilitar.'
                  : undefined
              }
            >
              <FiPlus size={24} />
              {isUploadingDraft
                ? 'Enviando...'
                : editingIndex !== null
                  ? 'Salvar alterações'
                  : 'Adicionar tempo de contribuição'}
            </Button>

            {editingIndex !== null && (
              <button
                type="button"
                className="inline-flex w-fit px-3 py-1 items-center gap-2 border rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                onClick={handleCancelEdit}
                title="Cancelar edição"
                disabled={isUploadingDraft}
              >
                <FiX size={18} />
                Cancelar edição
              </button>
            )}
          </div>
        </div>

        {servicesTime.length > 0 ? (
          <>
            {!canProceed && (
              <div className="mb-3 text-xs text-[#C53030] text-center max-w-[520px]">
                <p className="font-semibold">
                  Complete os dados e anexe os documentos obrigatórios de todos
                  os tempos para continuar.
                </p>
                <div className="mt-2 text-left">
                  {missingList.map((x) => (
                    <p key={`${x.index}-${x.msg}`}>
                      • Tempo #{x.index + 1}: {x.msg}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <Button
              type="button"
              fontSize="normal"
              color="blue"
              className="min-w-fit gap-3 px-10"
              onClick={() => navigate('/supporting-documents')}
              disabled={!canProceed || isUploadingDraft}
            >
              Avançar
              <FiArrowRightCircle size={24} />
            </Button>
          </>
        ) : (
          <Button
            type="button"
            fontSize="normal"
            color="pink"
            className="min-w-fit gap-3 px-10"
            onClick={() => navigate('/supporting-documents')}
            disabled={isUploadingDraft}
          >
            Não tenho tempo de contribuição anterior a ser declarado
            <FiArrowRightCircle size={24} />
          </Button>
        )}

        <button
          type="button"
          onClick={() => navigate('/beneficiary')}
          className="mb-[15px] flex flex-col items-center justify-center border-0 border-b border-transparent bg-transparent text-[#333] hover:border-[#333] focus:border-[#333]"
          disabled={isUploadingDraft}
        >
          &lt; Anterior
        </button>

        <ModalBox
          isOpen={!!draftDocsError}
          onRequestClose={() => setDraftDocsError('')}
        >
          <p>{draftDocsError}</p>
        </ModalBox>
      </div>
    </>
  )
}

export default ServiceTime
